'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Stethoscope, User, Shield, ArrowRight, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import ClinigolLogo from '@/components/ui/ClinigolLogo';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

// Module-level client — avoids creating a second GoTrueClient instance
const supabase = createClient();

type AuthMode = 'login' | 'register';
type UserRole = 'admin' | 'vet' | 'owner';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  rcvsNumber?: string;
  practiceName?: string;
  practicePostcode?: string;
  invitationCode?: string;
  agreeTerms: boolean;
}

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [role, setRole] = useState<UserRole>('vet');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({ defaultValues: { remember: false } });
  const registerForm = useForm<RegisterFormData>();

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back to ClinigolHub.');

      // Determine role — check user_profiles first, fall back to metadata
      let role = 'owner';
      try {
        const userId = result?.user?.id;
        if (userId) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', userId)
            .single();
          if (profile?.role) {
            role = profile.role;
          } else {
            role =
              result?.user?.user_metadata?.role ??
              result?.user?.app_metadata?.role ??
              'owner';
          }
        }
      } catch {
        role =
          result?.user?.user_metadata?.role ??
          result?.user?.app_metadata?.role ??
          'owner';
      }

      // Write role to sessionStorage so AuthContext reads it instantly
      // on the destination page — prevents any spinner flash
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('clinigol_user_role', role);
      }

      if (role === 'admin' || role === 'vet') {
        router.push('/admin-dashboard');
      } else {
        router.push('/trial-submission-form');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Invalid email or password.');
      loginForm.setError('email', { message: error?.message || 'Invalid email or password.' });
    } finally {
      setIsLoading(false);
    }
  };

  const onRegisterSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`,
        role,
      });
      toast.success('Registration submitted — an admin will review your application shortly.');
      setMode('login');
    } catch (error: any) {
      toast.error(error?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions: { key: UserRole; label: string; icon: React.ElementType; desc: string }[] = [
    { key: 'admin', label: 'Admin', icon: Shield, desc: 'Platform administration' },
    { key: 'vet', label: 'Veterinarian', icon: Stethoscope, desc: 'Clinical access & CPD' },
    { key: 'owner', label: 'Pet Owner', icon: User, desc: 'Trial participant' },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: '#f8f7f9' }}>
      {/* ── Left atmospheric panel ── */}
      <div
        className="hidden lg:flex lg:w-[48%] xl:w-[44%] relative flex-col justify-between overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #c47a2b 0%, #a0527a 30%, #681f77 58%, #3b1060 80%, #1a0a2e 100%)',
        }}
      >
        {/* Grain / noise texture overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px 200px',
            opacity: 0.55,
            mixBlendMode: 'overlay',
          }}
        />
        {/* Soft vignette top */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, transparent 40%, rgba(0,0,0,0.25) 100%)' }} />
        {/* Subtle star-dot scatter */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.55) 1px, transparent 1px)',
          backgroundSize: '38px 38px',
          opacity: 0.12,
        }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
          {/* Logo lockup */}
          <div className="flex items-center gap-3 mb-auto">
            <div className="flex items-center flex-shrink-0">
              <ClinigolLogo width={130} height={44} color="rgba(255,255,255,0.92)" />
            </div>
          </div>

          {/* Hero headline — editorial mix */}
          <div className="mt-16 mb-8">
            <p className="text-xs font-medium tracking-widest uppercase mb-5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'var(--font-geist-sans)', letterSpacing: '0.14em' }}>
              ■ &nbsp;Advancing canine cognitive science
            </p>
            <h1 style={{ fontFamily: 'var(--font-newsreader)', fontSize: 'clamp(2rem, 3.2vw, 2.9rem)', lineHeight: 1.12, letterSpacing: '-0.025em', color: '#ffffff', fontWeight: 400 }}>
              Pulling the future{' '}
              <em style={{ fontStyle: 'italic', fontWeight: 300 }}>forward.</em>
            </h1>
            <p className="mt-5 text-sm leading-relaxed max-w-xs" style={{ color: 'rgba(255,255,255,0.62)', fontFamily: 'var(--font-geist-sans)' }}>
              We partner with vets and pet owners to build evidence that changes lives — structured trials, rigorous data, and compassionate science.
            </p>
          </div>

          {/* Active trial badge */}
          <div
            className="inline-flex items-start gap-3 rounded-2xl px-4 py-3.5 mb-10 self-start"
            style={{ background: 'rgba(0,0,0,0.22)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.14)' }}
          >
            <FlaskConical className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.55)' }} />
            <div>
              <p className="text-white text-xs font-semibold" style={{ fontFamily: 'var(--font-geist-sans)' }}>Trial MFY-001 · Mindful Years</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'var(--font-geist-sans)' }}>54 participants · Actively recruiting</p>
            </div>
          </div>

          {/* Testimonial card */}
          <div
            className="rounded-2xl p-5 mb-8"
            style={{ background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            <p
              className="text-sm leading-relaxed mb-4"
              style={{ color: 'rgba(255,255,255,0.78)', fontFamily: 'var(--font-newsreader)', fontStyle: 'italic', fontSize: '0.9375rem' }}
            >
              &ldquo;The structured approach to tracking Barley&apos;s cognitive changes has given us something to hold onto — real data that shows progress.&rdquo;
            </p>
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.18)' }}
              >
                <span className="text-white text-xs font-bold" style={{ fontFamily: 'var(--font-geist-sans)' }}>HO</span>
              </div>
              <div>
                <p className="text-white text-xs font-semibold" style={{ fontFamily: 'var(--font-geist-sans)' }}>Harriet Osei</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-geist-sans)' }}>MFY-001 Participant · Barley&apos;s owner</p>
              </div>
            </div>
          </div>

          {/* Footer sub-brand */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.32)', fontFamily: 'var(--font-geist-sans)', letterSpacing: '0.06em' }}>Powered by CNS · Clinigol Nutrition Solutions</span>
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.12)' }} />
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div
        className="flex-1 flex items-start justify-center overflow-y-auto"
        style={{ background: '#ffffff' }}
      >
        <div className="w-full max-w-[440px] px-6 py-12 lg:py-16">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <ClinigolLogo width={110} height={36} color="#681f77" />
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h2
              style={{
                fontFamily: 'var(--font-newsreader)',
                fontSize: 'clamp(1.6rem, 2.5vw, 2rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.025em',
                color: '#1e1e1e',
                fontWeight: 400,
                marginBottom: '0.5rem',
              }}
            >
              {mode === 'login' ? (
                <>Welcome <em style={{ fontStyle: 'italic', fontWeight: 300 }}>back.</em></>
              ) : (
                <>Create your <em style={{ fontStyle: 'italic', fontWeight: 300 }}>account.</em></>
              )}
            </h2>
            <p className="text-sm" style={{ color: '#777777', fontFamily: 'var(--font-geist-sans)' }}>
              {mode === 'login' ? 'Sign in to access the Clinigol clinical portal.' : 'Register to participate in Clinigol trials and CPD.'}
            </p>
          </div>

          {/* Mode toggle */}
          <div
            className="flex items-center gap-1 p-1 rounded-xl mb-7"
            style={{ background: '#f0edf3', border: '1px solid #e4dde9' }}
          >
            <button
              onClick={() => setMode('login')}
              className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200"
              style={
                mode === 'login'
                  ? { background: '#681f77', color: '#ffffff', fontFamily: 'var(--font-geist-sans)' }
                  : { color: '#888888', fontFamily: 'var(--font-geist-sans)' }
              }
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200"
              style={
                mode === 'register'
                  ? { background: '#681f77', color: '#ffffff', fontFamily: 'var(--font-geist-sans)' }
                  : { color: '#888888', fontFamily: 'var(--font-geist-sans)' }
              }
            >
              Register
            </button>
          </div>

          {/* Role selector */}
          <div className="grid grid-cols-3 gap-2 mb-7">
            {roleOptions.map((opt) => {
              const RoleIcon = opt.icon;
              const isSelected = role === opt.key;
              return (
                <button
                  key={`role-${opt.key}`}
                  onClick={() => setRole(opt.key)}
                  className="flex flex-col items-center gap-2 p-3.5 rounded-xl transition-all duration-200"
                  style={
                    isSelected
                      ? {
                          border: '1px solid rgba(104,31,119,0.5)',
                          background: 'rgba(104,31,119,0.08)',
                          color: '#681f77',
                        }
                      : {
                          border: '1px solid #e4dde9',
                          background: '#f8f5fa',
                          color: '#aaa0b5',
                        }
                  }
                >
                  <RoleIcon className="w-4 h-4" />
                  <span className="text-xs font-semibold" style={{ fontFamily: 'var(--font-geist-sans)' }}>{opt.label}</span>
                  <span className="text-xs leading-none text-center hidden sm:block" style={{ color: isSelected ? 'rgba(104,31,119,0.6)' : '#bbb0c5', fontFamily: 'var(--font-geist-sans)' }}>{opt.desc}</span>
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1" style={{ background: '#e4dde9' }} />
            <span className="text-xs" style={{ color: '#bbb0c5', fontFamily: 'var(--font-geist-sans)' }}>credentials</span>
            <div className="h-px flex-1" style={{ background: '#e4dde9' }} />
          </div>

          {mode === 'login' ? (
            <LoginForm
              form={loginForm}
              onSubmit={onLoginSubmit}
              isLoading={isLoading}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
            />
          ) : (
            <RegisterForm
              form={registerForm}
              onSubmit={onRegisterSubmit}
              isLoading={isLoading}
              role={role}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              showConfirmPassword={showConfirmPassword}
              onToggleConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
            />
          )}

          <p className="text-xs text-center mt-7" style={{ color: '#aaa0b5', fontFamily: 'var(--font-geist-sans)' }}>
            By continuing, you agree to Clinigol&apos;s{' '}
            <a href="#" className="hover:underline transition-colors" style={{ color: '#681f77' }}>Terms of Service</a>{' '}
            and{' '}
            <a href="#" className="hover:underline transition-colors" style={{ color: '#681f77' }}>Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

function LoginForm({ form, onSubmit, isLoading, showPassword, onTogglePassword }: {
  form: any;
  onSubmit: (data: LoginFormData) => void;
  isLoading: boolean;
  showPassword: boolean;
  onTogglePassword: () => void;
}) {
  const { register, handleSubmit, formState: { errors } } = form;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <label className="block text-xs font-medium mb-2 tracking-wide" style={{ color: '#777777', fontFamily: 'var(--font-geist-sans)', letterSpacing: '0.04em' }}>
          EMAIL ADDRESS
        </label>
        <input
          type="email"
          className="input-field"
          placeholder="you@clinigol.vet"
          {...register('email', { required: 'Email is required' })}
        />
        {errors.email && <p className="text-xs mt-1.5" style={{ color: '#dc2626', fontFamily: 'var(--font-geist-sans)' }}>{errors.email.message}</p>}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium tracking-wide" style={{ color: '#777777', fontFamily: 'var(--font-geist-sans)', letterSpacing: '0.04em' }}>
            PASSWORD
          </label>
          <a href="#" className="text-xs transition-colors hover:opacity-80" style={{ color: '#681f77', fontFamily: 'var(--font-geist-sans)' }}>Forgot password?</a>
        </div>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            className="input-field pr-10"
            placeholder="••••••••••"
            {...register('password', { required: 'Password is required' })}
          />
          <button type="button" onClick={onTogglePassword} className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#aaa0b5' }}>
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {errors.password && <p className="text-xs mt-1.5" style={{ color: '#dc2626', fontFamily: 'var(--font-geist-sans)' }}>{errors.password.message}</p>}
      </div>
      <div className="flex items-center gap-2.5">
        <input type="checkbox" id="remember" {...register('remember')} className="w-4 h-4 rounded accent-primary" />
        <label htmlFor="remember" className="text-sm" style={{ color: '#666666', fontFamily: 'var(--font-geist-sans)' }}>Remember me for 30 days</label>
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
        style={{ background: '#681f77', color: '#ffffff', fontFamily: 'var(--font-geist-sans)' }}
      >
        {isLoading ? (
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>Sign In <ArrowRight className="w-4 h-4" /></>
        )}
      </button>
    </form>
  );
}

function RegisterForm({ form, onSubmit, isLoading, role, showPassword, onTogglePassword, showConfirmPassword, onToggleConfirmPassword }: {
  form: any;
  onSubmit: (data: RegisterFormData) => void;
  isLoading: boolean;
  role: UserRole;
  showPassword: boolean;
  onTogglePassword: () => void;
  showConfirmPassword: boolean;
  onToggleConfirmPassword: () => void;
}) {
  const { register, handleSubmit, watch, formState: { errors } } = form;
  const password = watch('password');

  const labelClass = "block text-xs font-medium mb-2 tracking-wide";
  const labelStyle = { color: '#777777', fontFamily: 'var(--font-geist-sans)', letterSpacing: '0.04em' };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass} style={labelStyle}>FIRST NAME</label>
          <input type="text" className="input-field" placeholder="Eleanor" {...register('firstName', { required: 'Required' })} />
          {errors.firstName && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{errors.firstName.message}</p>}
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>LAST NAME</label>
          <input type="text" className="input-field" placeholder="Hartley" {...register('lastName', { required: 'Required' })} />
          {errors.lastName && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{errors.lastName.message}</p>}
        </div>
      </div>
      <div>
        <label className={labelClass} style={labelStyle}>EMAIL ADDRESS</label>
        <input type="email" className="input-field" placeholder={role === 'vet' ? 'you@vetpractice.co.uk' : 'you@email.com'} {...register('email', { required: 'Email is required' })} />
        {errors.email && <p className="text-xs mt-1.5" style={{ color: '#dc2626' }}>{errors.email.message}</p>}
      </div>

      {role === 'vet' && (
        <>
          <div>
            <label className={labelClass} style={labelStyle}>RCVS REGISTRATION NUMBER</label>
            <p className="text-xs mb-2" style={{ color: '#aaa0b5', fontFamily: 'var(--font-geist-sans)' }}>Required for verification before access is granted.</p>
            <input type="text" className="input-field font-mono" placeholder="RCVS-2020-XXXX" {...register('rcvsNumber', { required: role === 'vet' ? 'RCVS number is required for vets' : false })} />
            {errors.rcvsNumber && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{errors.rcvsNumber.message}</p>}
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>PRACTICE NAME</label>
            <input type="text" className="input-field" placeholder="Southfields Veterinary Clinic" {...register('practiceName', { required: role === 'vet' ? 'Practice name is required' : false })} />
            {errors.practiceName && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{errors.practiceName.message}</p>}
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>PRACTICE POSTCODE</label>
            <input type="text" className="input-field" placeholder="SW18 1AX" {...register('practicePostcode')} />
          </div>
        </>
      )}

      {role === 'owner' && (
        <div>
          <label className={labelClass} style={labelStyle}>INVITATION CODE</label>
          <p className="text-xs mb-2" style={{ color: '#aaa0b5', fontFamily: 'var(--font-geist-sans)' }}>Your vet will have provided an invitation code when enrolling you in a trial.</p>
          <input type="text" className="input-field font-mono" placeholder="MFY-INV-XXXX" {...register('invitationCode', { required: role === 'owner' ? 'Invitation code is required' : false })} />
          {errors.invitationCode && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{errors.invitationCode.message}</p>}
        </div>
      )}

      <div>
        <label className={labelClass} style={labelStyle}>PASSWORD</label>
        <div className="relative">
          <input type={showPassword ? 'text' : 'password'} className="input-field pr-10" placeholder="Minimum 10 characters" {...register('password', { required: 'Password is required', minLength: { value: 10, message: 'Password must be at least 10 characters' } })} />
          <button type="button" onClick={onTogglePassword} className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#aaa0b5' }}>
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {errors.password && <p className="text-xs mt-1.5" style={{ color: '#dc2626' }}>{errors.password.message}</p>}
      </div>
      <div>
        <label className={labelClass} style={labelStyle}>CONFIRM PASSWORD</label>
        <div className="relative">
          <input type={showConfirmPassword ? 'text' : 'password'} className="input-field pr-10" placeholder="Repeat your password" {...register('confirmPassword', { required: 'Please confirm your password', validate: (v: string) => v === password || 'Passwords do not match' })} />
          <button type="button" onClick={onToggleConfirmPassword} className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#aaa0b5' }}>
            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-xs mt-1.5" style={{ color: '#dc2626' }}>{errors.confirmPassword.message}</p>}
      </div>

      <div className="flex items-start gap-2.5">
        <input type="checkbox" id="agreeTerms" {...register('agreeTerms', { required: 'You must agree to the terms' })} className="w-4 h-4 mt-0.5 rounded accent-primary" />
        <label htmlFor="agreeTerms" className="text-sm leading-relaxed" style={{ color: '#666666', fontFamily: 'var(--font-geist-sans)' }}>
          I agree to Clinigol&apos;s{' '}
          <a href="#" className="hover:underline" style={{ color: '#681f77' }}>Terms of Service</a>{' '}
          and{' '}
          <a href="#" className="hover:underline" style={{ color: '#681f77' }}>Privacy Policy</a>
        </label>
      </div>
      {errors.agreeTerms && <p className="text-xs" style={{ color: '#dc2626' }}>{errors.agreeTerms.message}</p>}

      {role !== 'owner' && (
        <div
          className="rounded-xl p-3.5 text-xs"
          style={{ background: 'rgba(104,31,119,0.06)', border: '1px solid rgba(104,31,119,0.15)', color: '#681f77', fontFamily: 'var(--font-geist-sans)' }}
        >
          Your registration will be reviewed by a Clinigol admin before access is granted. You&apos;ll receive an email within 2 working days.
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: '#681f77', color: '#ffffff', fontFamily: 'var(--font-geist-sans)' }}
      >
        {isLoading ? (
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>Submit Registration <ArrowRight className="w-4 h-4" /></>
        )}
      </button>
    </form>
  );
}